import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../config/api';
import { extractErrorMessage } from '../utils/extractError';

interface Section {
  heading: string;
  content: string;
  type: string;
}

interface Report {
  id: number;
  store_id: number;
  title: string;
  report_date: string;
  report_type: string;
  status: string;
  summary: string;
  sections: Section[];
  metrics: Record<string, number> | null;
  attachments: string[];
  monitor_name?: string;
  created_at: string;
  updated_at?: string;
  published_at?: string;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) + ' at ' + date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatReportType(type: string): string {
  return type.charAt(0).toUpperCase() + type.slice(1);
}

const METRIC_LABELS: Record<string, string> = {
  total_alerts: 'Total Alerts',
  employees_seen: 'Employees Seen',
  incidents_count: 'Incidents',
};

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  draft: { label: 'Draft', color: 'var(--status-warning)', bg: 'var(--status-warning-muted)' },
  published: { label: 'Published', color: 'var(--status-success)', bg: 'var(--status-success-muted)' },
};

export default function ReportDetailPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();

  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchReport = useCallback(async () => {
    try {
      const res = await api.get(`/reports/${reportId}`);
      setReport(res.data);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load report.'));
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  async function handlePublish() {
    if (!report) return;
    setPublishing(true);
    try {
      await api.put(`/reports/${reportId}`, { ...report, status: 'published' });
      setReport({ ...report, status: 'published' });
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to publish report.'));
    } finally {
      setPublishing(false);
    }
  }

  async function handleDelete() {
    if (!report || !window.confirm('Delete this draft report?')) return;
    setDeleting(true);
    try {
      await api.delete(`/reports/${reportId}`);
      navigate(`/stores/${report.store_id}`);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete report.'));
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.page}>
        <main style={styles.main}>
          <p style={styles.loadingText}>Loading report...</p>
        </main>
      </div>
    );
  }

  if (!report) {
    return (
      <div style={styles.page}>
        <main style={styles.main}>
          <div style={styles.errorBanner}>{error || 'Report not found.'}</div>
          <button onClick={() => navigate(-1)} style={styles.backButton}>← Go Back</button>
        </main>
      </div>
    );
  }

  const sConfig = STATUS_CONFIG[report.status] || STATUS_CONFIG.draft;
  const isDraft = report.status === 'draft';

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <button
          onClick={() => navigate(`/stores/${report.store_id}`)}
          style={styles.backButton}
        >
          ← Back to Store
        </button>

        {error && <div style={styles.errorBanner}>{error}</div>}

        <div style={styles.card}>
          {/* Report Header */}
          <div style={styles.reportHeader}>
            <div style={styles.reportMeta}>
              <span style={{
                ...styles.statusBadge,
                background: sConfig.bg,
                color: sConfig.color,
              }}>
                {sConfig.label}
              </span>
              <span style={styles.reportType}>{formatReportType(report.report_type)}</span>
              <span style={styles.reportDate}>{formatDate(report.report_date)}</span>
            </div>
            <h2 style={styles.reportTitle}>{report.title}</h2>
            {report.monitor_name && (
              <p style={styles.monitorName}>By {report.monitor_name}</p>
            )}
            {report.published_at && (
              <p style={styles.publishedAt}>Published on {formatTimestamp(report.published_at)}</p>
            )}
          </div>

          {/* Summary */}
          {report.summary && (
            <div style={styles.summarySection}>
              <h3 style={styles.sectionTitle}>Summary</h3>
              <p style={styles.summaryText}>{report.summary}</p>
            </div>
          )}

          {/* Sections */}
          {report.sections && report.sections.length > 0 && (
            <div style={styles.sectionsContainer}>
              {report.sections.map((section, i) => (
                <div key={i} style={styles.sectionBlock}>
                  <h3 style={styles.sectionTitle}>{section.heading}</h3>
                  <p style={styles.sectionContent}>{section.content}</p>
                </div>
              ))}
            </div>
          )}

          {/* Metrics */}
          {report.metrics && Object.keys(report.metrics).length > 0 && (
            <div style={styles.metricsSection}>
              <h3 style={styles.sectionTitle}>Metrics</h3>
              <div style={styles.metricsGrid}>
                {Object.entries(report.metrics).map(([key, value]) => (
                  <div key={key} style={styles.metricCard}>
                    <span style={styles.metricValue}>{value}</span>
                    <span style={styles.metricLabel}>
                      {METRIC_LABELS[key] || key.replace(/_/g, ' ')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attachments */}
          {report.attachments && report.attachments.length > 0 && (
            <div style={styles.attachmentsSection}>
              <h3 style={styles.sectionTitle}>Attachments</h3>
              <div style={styles.attachmentList}>
                {report.attachments.map((key, i) => (
                  <div key={i} style={styles.attachmentItem}>
                    <span style={styles.attachmentIcon}>📎</span>
                    <span style={styles.attachmentName}>{key.split('/').pop() || key}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          {isDraft && (
            <div style={styles.actions}>
              <button
                onClick={handleDelete}
                disabled={deleting || publishing}
                style={styles.deleteButton}
              >
                {deleting ? 'Deleting...' : 'Delete Draft'}
              </button>
              <button
                onClick={() => navigate(`/reports/${reportId}/edit`)}
                disabled={publishing}
                style={styles.editButton}
              >
                Edit
              </button>
              <button
                onClick={handlePublish}
                disabled={publishing || deleting}
                style={styles.publishButton}
              >
                {publishing ? 'Publishing...' : 'Publish Report'}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: 'var(--bg-base)' },
  main: { maxWidth: 800, margin: '0 auto', padding: 'var(--space-8) var(--space-6)' },
  backButton: {
    background: 'none', border: 'none', color: 'var(--accent-text)',
    fontSize: 'var(--text-md)', fontWeight: 500, fontFamily: 'var(--font-body)', cursor: 'pointer', padding: 0, marginBottom: 'var(--space-6)',
  },
  loadingText: { color: 'var(--text-secondary)', fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)' },
  errorBanner: {
    background: 'var(--status-critical-muted)', color: 'var(--status-critical)', padding: '12px 16px',
    borderRadius: 'var(--radius-lg)', fontSize: 'var(--text-base)', fontFamily: 'var(--font-body)',
    border: '1px solid rgba(255,71,87,0.3)', marginBottom: 'var(--space-5)',
  },
  card: {
    background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border-subtle)',
    padding: '32px 32px 28px',
  },
  // Report Header
  reportHeader: { marginBottom: 28 },
  reportMeta: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 },
  statusBadge: {
    padding: '3px 10px', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-xs)', fontWeight: 700,
    fontFamily: 'var(--font-body)', letterSpacing: 0.5,
  },
  reportType: {
    fontSize: 'var(--text-sm)', fontWeight: 600, fontFamily: 'var(--font-body)', color: 'var(--text-secondary)',
    background: 'var(--bg-surface-raised)', padding: '2px 8px', borderRadius: 'var(--radius-sm)',
  },
  reportDate: { marginLeft: 'auto', fontSize: 'var(--text-base)', fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' },
  reportTitle: { fontSize: 'var(--text-xl)', fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--text-primary)', margin: '0 0 6px' },
  monitorName: { fontSize: 'var(--text-base)', fontFamily: 'var(--font-body)', color: 'var(--text-tertiary)', margin: '0 0 4px' },
  publishedAt: { fontSize: 'var(--text-sm)', fontFamily: 'var(--font-body)', color: 'var(--status-success)', margin: 0 },
  // Summary
  summarySection: {
    marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid var(--border-subtle)',
  },
  summaryText: { fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', lineHeight: 1.7, margin: 0 },
  // Sections
  sectionsContainer: { display: 'flex', flexDirection: 'column', gap: 24 },
  sectionBlock: {
    background: 'var(--bg-surface-raised)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-5)',
    marginBottom: 4,
  },
  sectionTitle: {
    fontSize: 'var(--text-md)', fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--text-primary)', margin: '0 0 10px',
  },
  sectionContent: { fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' },
  // Metrics
  metricsSection: { marginTop: 24, paddingTop: 24, borderTop: '1px solid var(--border-subtle)' },
  metricsGrid: { display: 'flex', gap: 16 },
  metricCard: {
    flex: 1, background: 'var(--bg-surface-raised)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-subtle)',
    borderLeft: '3px solid var(--accent)', padding: '18px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
  },
  metricValue: { fontSize: 'var(--text-2xl)', fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--accent-text)' },
  metricLabel: { fontSize: 'var(--text-sm)', fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', fontWeight: 500, textTransform: 'capitalize' },
  // Attachments
  attachmentsSection: { marginTop: 24, paddingTop: 24, borderTop: '1px solid var(--border-subtle)' },
  attachmentList: { display: 'flex', flexDirection: 'column', gap: 6 },
  attachmentItem: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
    background: 'var(--bg-surface-raised)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)',
  },
  attachmentIcon: { fontSize: 14 },
  attachmentName: { fontSize: 'var(--text-base)', fontFamily: 'var(--font-body)', color: 'var(--text-primary)' },
  // Actions
  actions: {
    display: 'flex', justifyContent: 'flex-end', gap: 12,
    marginTop: 28, paddingTop: 24, borderTop: '1px solid var(--border-subtle)',
  },
  deleteButton: {
    padding: '10px 20px', borderRadius: 'var(--radius-lg)', border: 'none',
    background: 'transparent', fontSize: 'var(--text-md)', fontWeight: 500, fontFamily: 'var(--font-body)',
    color: 'var(--status-critical)', cursor: 'pointer', marginRight: 'auto',
  },
  editButton: {
    padding: '10px 22px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-default)',
    background: 'var(--bg-surface-raised)', fontSize: 'var(--text-md)', fontWeight: 600, fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)', cursor: 'pointer',
  },
  publishButton: {
    padding: '10px 24px', borderRadius: 'var(--radius-lg)', border: 'none',
    background: 'var(--accent)', color: '#fff', fontSize: 'var(--text-md)', fontWeight: 600, fontFamily: 'var(--font-body)',
    cursor: 'pointer',
  },
};
